/**
 * `My Notes` — every note this manager has written, for the whole tournament, in one place.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS SCREEN EXISTS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate's own finding, from live UM-side testing on 2026-09-09: *"there is
 * nowhere on the app UI for me as a UM to go back to view the feedbacks I have added."* He was
 * right, and it was a structural gap rather than a missing button. Until this run a note could
 * be read in exactly one place — inside the capture screen of the match it was written on — so
 * answering *"what have I written today?"* meant remembering which matches you had opened and
 * opening each of them again. A manager who has captured across three pitches cannot do that,
 * and a manager who cannot review what they wrote cannot be expected to trust that it is there.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT IS, AND WHAT IT DELIBERATELY IS NOT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It is a **read**. Every control that changes a note — `Edit`, `🔒 Lock`, `Release`, the §H
 * unlock-and-release warning, the review gate for a native-language note, and `Undo Release` —
 * lives on the capture screen (spec 03, built into `CapturePanel`) and stays there. This page
 * does not duplicate one of them, and it must not: two screens offering the same consequential
 * press is two places for the release semantics to drift, and the release gate is the single
 * most consequential control in the product. What each match group carries instead is one
 * `Open Match ›` link to the screen that owns those presses.
 *
 * **THE 15-MINUTE UNDO IS NAMED HERE AND NOT OFFERED HERE** (TD authorisation, 9 September
 * 2026 — spec 11 §2's dated amendment), and that split was a deliberate judgement rather than
 * an omission. A manager who has just released across three pitches is exactly the person who
 * comes to this page to check what went out, and telling them nothing about a window that is
 * running would be this page's original gap in a new form. Putting the BUTTON here, though,
 * would give the release lifecycle a second home — the thing the paragraph above exists to
 * prevent — for a control whose whole design depends on a countdown that is honest about a
 * server's clock. So a released row inside its window carries the sentence
 * `Undo Available · 12 Min Left` and the group's own `Open Match ›` is the way to act on it.
 * The row says what is true and points at the one screen that can change it.
 *
 * **`Release All Outstanding` is not here either.** That is spec 11 §4's 21:00 flow — a
 * cross-match bulk release with its own reminder, its own scope and its own receipt — and it is
 * out of scope for this run. There are no bulk actions on this page at all.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 78 — A NOTE OPENS IN PLACE, AND THE READ-ONLY RULE IS UNTOUCHED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The Technical Delegate, live-testing on an iPhone: *"could you make all the notes clickable so
 * I don't always have to go into each match summary to view these individual notes."* The list
 * this page shipped with answered *"what have I written?"* but not *"what exactly did I write?"*
 * — a row carried the English line and nothing else, so the manager's own words in their own
 * language, the criterion, the tags and the times were all still a screen away, and reading one
 * note cost a navigation and a scroll through a capture composer.
 *
 * A row is now a disclosure. Tapping it expands the whole note underneath, in place, and tapping
 * it again closes it; several may be open at once, because comparing two notes is the reason a
 * manager is on this screen rather than on the match. **This changes nothing about the rule
 * above.** Everything the expansion adds is a READ of fields `OwnNoteView` already carries —
 * `bodyNative`, `criterionStableKey`, `tagIds`, `capturedAt`, `releasedAt` — and there is still
 * no `Edit`, no `Lock`, no `Release` and no bulk press anywhere on this page. No endpoint was
 * added, changed or newly called: the same `GET /notes/mine` that drew the list draws the
 * detail, because it always sent the whole note.
 *
 * The expansion carries one control, and like the group's own it is a way OUT: `Open In Match ›`
 * is the group's `href` with `#note-<id>` on the end, and the capture screen lands on that row
 * and pulses it. So the manager who reads a note here and decides to change it arrives beside
 * the note itself rather than at the top of the match, and the presses are still only ever
 * offered on the one screen that owns them.
 *
 * The disclosure control is a `<button>` and the expansion is its SIBLING inside the `<li>`, not
 * its child. An `<a>` inside a `<button>` is invalid HTML and gives a keyboard user a control
 * they cannot reach past, and the jump link is the one thing in the expansion that must stay
 * reachable — so the two are siblings, and `aria-controls` is what ties them together.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE COUNTS, AND WHOSE THEY ARE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `N Notes · N Draft · N Locked · N Released`, over the caller's own notes and nobody else's.
 * Ruling R4″ — the disclosure that the TD can count a manager's notes — has nothing to say here:
 * this is the author's own surface, reading the author's own list, and it is the one place where
 * a total over drafts and locked notes is simply the author looking at their own work.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO READS, ONE CLIENT-SIDE JOIN, AND NO NEW ENDPOINT
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `GET /notes/mine` with no parameters already serves exactly this: the route's `match`,
 * `umpire`, `competition` and `search` filters have always all been optional, the repository
 * orders by `captured_at, id`, and the route is author-scoped by the credential rather than by a
 * query field. **So no endpoint was added and none was changed** — the API's visibility
 * semantics are untouched by this run.
 *
 * A note carries `matchId` and `competitionId` but not the match's number or its date, so the
 * grouping is joined here against `GET /fixtures` — the same whole-schedule read the Today
 * screen uses to find the day's matches. The join is client-side and it is honest about failing:
 * if the schedule cannot be read, every group falls back to `Match {id}` and the notes are all
 * still listed. A note whose match is genuinely absent from the schedule does the same, in a
 * final group of its own, because a note the author wrote must never be the thing this screen
 * hides to keep its own layout tidy.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  CAPTURE_TAP_TAGS,
  languageByCode,
  undoWindowIsOpen,
  undoWindowLabel,
  undoWindowRemainingMs,
} from "@hockeytd/shared";

import { fetchFixtureDays, fetchMyNotes } from "../../api/client.js";
import type { FixtureRowView, OwnNoteView } from "../../api/types.js";
import { AI_DRAFT_TAG } from "../../features/um/NativeNoteReview.js";
import { HowToLink, HowToUse } from "../../app/HowToUse.js";
import { criterionName } from "./matchDebrief.js";
import { FailBanner, Skeleton } from "../../app/states.js";
import { useAsync } from "../../app/useAsync.js";
import { UM_ROOT_PATH } from "../../app/UmShell.js";
import { tokyoDateKicker } from "./UmTodayPage.js";

/** §H's three labels, verbatim, with the house pill tone each one wears on the capture screen. */
const STATE_LABEL: Readonly<Record<OwnNoteView["visibility"], string>> = {
  draft: "Draft",
  locked: "Locked",
  released: "Released",
};

/**
 * `Draft` is muted, `Locked` amber, `Released` green — the same three tones the capture screen's
 * own sections read as, and never colour alone: the word is in the chip.
 */
const STATE_TONE: Readonly<Record<OwnNoteView["visibility"], string>> = {
  draft: "statuspill--muted",
  locked: "statuspill--warn",
  released: "statuspill--good",
};

/** `18 September` — the short form the match group's own label carries. */
export function shortDateLabel(date: string): string {
  const parsed = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "long",
  }).format(parsed);
}

export interface MyNotesMatchGroup {
  matchId: number;
  /** `M01 · Men's`, or `Match 22889` for a match the schedule does not carry. */
  label: string;
  /**
   * `M01 · Men's · 18 September` — the whole identification of the match in one string.
   *
   * The date is drawn ONCE, on the matchday heading this group sits under, rather than repeated
   * on every group inside that day. This is the composite the accessible name uses, so a screen
   * reader landing on a group hears which match, which competition and which day without having
   * to walk back up to the heading.
   */
  fullLabel: string;
  /** The capture screen, competition carried — the screen that owns every press on these notes. */
  href: string;
  notes: readonly OwnNoteView[];
}

export interface MyNotesDay {
  /** `null` for the group holding notes whose match is not in the schedule. */
  date: string | null;
  heading: string;
  matches: readonly MyNotesMatchGroup[];
}

export interface MyNotesCounts {
  total: number;
  draft: number;
  locked: number;
  released: number;
}

export function myNotesCounts(notes: readonly OwnNoteView[]): MyNotesCounts {
  return {
    total: notes.length,
    draft: notes.filter((note) => note.visibility === "draft").length,
    locked: notes.filter((note) => note.visibility === "locked").length,
    released: notes.filter((note) => note.visibility === "released").length,
  };
}

/** `12 Notes · 5 Draft · 2 Locked · 5 Released`. Singular `1 Note` exists, as it must. */
export function myNotesCountLine(counts: MyNotesCounts): string {
  return [
    `${String(counts.total)} ${counts.total === 1 ? "Note" : "Notes"}`,
    `${String(counts.draft)} Draft`,
    `${String(counts.locked)} Locked`,
    `${String(counts.released)} Released`,
  ].join(" · ");
}

/**
 * The grouping, as a pure function of the two reads — so the arithmetic is tested without a
 * router, and the page below is only the drawing of it.
 *
 * Ordering: matchdays ascending, so a manager scrolls the tournament in the order it happened;
 * matches within a day by kick-off time, then by match number, which is the order the Today
 * screen lists them in; notes within a match in capture order, which is the order the API
 * already returns and is not re-sorted here.
 *
 * The unknown-match group is LAST and is never merged into a real day: it has no date, and
 * putting it under a guessed one would be inventing a fact about a note.
 */
export function groupMyNotes(
  notes: readonly OwnNoteView[],
  fixtures: readonly FixtureRowView[],
): readonly MyNotesDay[] {
  const byMatch = new Map<number, FixtureRowView>();
  for (const fixture of fixtures) byMatch.set(fixture.tmsMatchDbId, fixture);

  const matchIds = [...new Set(notes.map((note) => note.matchId))].filter(
    (matchId): matchId is number => matchId !== null,
  );

  const groups = matchIds.map((matchId) => {
    const fixture = byMatch.get(matchId);
    const rows = notes.filter((note) => note.matchId === matchId);
    // The competition comes from the fixture when there is one and from the note itself when
    // there is not — the note has always carried it, so the link is never guessed and never
    // dropped.
    const competition = fixture?.competitionId ?? rows[0]?.competitionId ?? "1916";
    const side = competition === "1916" ? "Men's" : "Women's";
    const label =
      fixture === undefined
        ? `Match ${String(matchId)}`
        : `${fixture.displayMatchNo} · ${side}`;
    return {
      matchId,
      label,
      fullLabel:
        fixture === undefined ? label : `${label} · ${shortDateLabel(fixture.date)}`,
      href: `${UM_ROOT_PATH}/match/${String(matchId)}?competition=${competition}`,
      notes: rows,
      date: fixture?.date ?? null,
      time: fixture?.time ?? "",
      sortNo: fixture?.displayMatchNo ?? String(matchId),
    };
  });

  const dates = [
    ...new Set(groups.map((group) => group.date).filter((date): date is string => date !== null)),
  ].sort();

  const days: MyNotesDay[] = dates.map((date) => ({
    date,
    heading: tokyoDateKicker(date),
    matches: groups
      .filter((group) => group.date === date)
      .sort((left, right) =>
        left.time === right.time
          ? left.sortNo.localeCompare(right.sortNo)
          : left.time.localeCompare(right.time),
      ),
  }));

  const orphans = groups
    .filter((group) => group.date === null)
    .sort((left, right) => left.matchId - right.matchId);
  if (orphans.length > 0) {
    days.push({
      date: null,
      // Said plainly, and said as a fact about the SCHEDULE rather than about the note: the
      // note is fine, it is the fixture we cannot find.
      heading: "Matches Not In The Schedule",
      matches: orphans,
    });
  }

  return days;
}

/** `4 Notes` — the day's own total, singular form included. */
export function dayCountLabel(day: MyNotesDay): string {
  const total = day.matches.reduce((sum, match) => sum + match.notes.length, 0);
  return `${String(total)} ${total === 1 ? "Note" : "Notes"}`;
}

/**
 * `Undo Available · 12 Min Left`, or nothing at all.
 *
 * The label is `undoWindowLabel`'s, so this page and the capture screen count the same minutes
 * the same way — rounded up, with the last minute named rather than counted. `null` the instant
 * the window closes, so a released row past its fifteen minutes reads exactly as every released
 * row has always read.
 */
export function undoHintFor(note: OwnNoteView, now: Date): string | null {
  if (note.visibility !== "released") return null;
  const label = undoWindowLabel(undoWindowRemainingMs(note.releasedAt, now));
  return label === null ? null : `Undo Available · ${label} Left`;
}

function NoteRow({
  note,
  now,
  matchHref,
}: {
  note: OwnNoteView;
  now: Date;
  matchHref: string;
}): React.JSX.Element {
  const undoHint = undoHintFor(note, now);
  /**
   * Open is per note, local to this row, and collapsed on arrival.
   *
   * Local rather than lifted because nothing outside the row has any business knowing: there
   * is no "expand all", no accordion that closes its neighbours, and no state to restore. Two
   * managers comparing the same umpire across two matches want both notes open at once, and a
   * page-level "the open one" would take the second away as they opened it.
   */
  const [open, setOpen] = useState(false);
  const panelId = `mynote-detail-${note.id}`;
  const modifier =
    note.visibility === "locked"
      ? " noterow--locked"
      : note.visibility === "released"
        ? " noterow--released"
        : "";
  return (
    <li className={`noterow${modifier}`} data-testid={`mynote-${note.id}`}>
      {/*
       * The whole row is the tap target — *"make all the notes clickable"* is answered by the
       * note being the button, not by a small chevron beside it that a thumb has to find.
       *
       * It carries only phrasing content, so it is valid HTML and so its accessible name is
       * the row a reader is looking at: the subject, the stamp and the note itself. That name
       * is left to come FROM the content rather than being overridden with an `aria-label`,
       * which would tell a voice-control user to say words that are not on the screen. What it
       * must never be is a bare `Expand`, repeated once per note — six identical buttons is a
       * list a screen-reader user cannot choose from.
       *
       * The state chip stays OUTSIDE the button, in the row's own actions strip. It is a
       * status and not part of the control, and putting `Locked` inside a button's name on the
       * one page that must offer no lock control would be the wrong word in the wrong place.
       */}
      <button
        type="button"
        className="mynote__open"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`mynote-open-${note.id}`}
        onClick={() => {
          setOpen((current) => !current);
        }}
      >
        {/* The name column identifies the SUBJECT — every note on this screen is the reader's
            own, exactly as on the capture screen (spec 03 §2.3). */}
        <span className="noterow__nm">
          {note.umpireMediaName ?? "General Note For This Match"}
        </span>
        <span className="noterow__min num">{noteStamp(note)}</span>
        {/* The affordance. Decorative and hidden from the accessible name, which `aria-expanded`
            already carries in words the reader's own software chooses. */}
        <span className="mynote__chev" aria-hidden="true">
          ›
        </span>
        <span
          className={
            note.visibility === "locked" ? "noterow__txt noterow__txt--muted" : "noterow__txt"
          }
        >
          {note.bodyEn}
        </span>
      </button>

      <div className="noterow__acts">
        {/* §H's label, verbatim, in the house pill. Word and colour, never colour alone (A4). */}
        <span
          className={`statuspill ${STATE_TONE[note.visibility]}`}
          data-testid={`mynote-state-${note.id}`}
        >
          <span className="sdot" />
          {STATE_LABEL[note.visibility]}
        </span>
        {/*
         * A SENTENCE, NOT A BUTTON — see the header. It says what is true of the note and
         * leaves the press to `Open Match ›`, which is on this group's own heading.
         */}
        {undoHint === null ? null : (
          <span className="mynote__undo" data-testid={`mynote-undo-${note.id}`}>
            {undoHint}
          </span>
        )}
      </div>

      {/*
       * Mounted only while it is open, which is the same choice `HowToUse` makes and for the
       * same reason: the page should read exactly as it looks. A panel that sat in the DOM
       * permanently would put a second copy of every note's English under every note.
       */}
      {open ? <NoteDetail note={note} matchHref={matchHref} panelId={panelId} /> : null}
    </li>
  );
}

/**
 * The opened note: the manager's own words, the English, how it is filed, and when.
 *
 * Every row is a READ of a field `OwnNoteView` already carries, and a row whose value is
 * absent is **omitted entirely** rather than drawn with a dash. A dash in a list of facts about
 * a note reads as a rendering failure; `Tags —` on a note that was never tagged is a blank
 * where a manager will look for the tags they remember choosing.
 */
function NoteDetail({
  note,
  matchHref,
  panelId,
}: {
  note: OwnNoteView;
  matchHref: string;
  panelId: string;
}): React.JSX.Element {
  /**
   * The language the note was written in, when it was written in one. `bodyNative` is what
   * decides the row exists; the language only names it, so a code the build does not know
   * costs the endonym in the label and never the manager's own words.
   */
  const language = note.nativeLang === null ? undefined : languageByCode(note.nativeLang);
  return (
    <div className="mynote__detail" id={panelId} data-testid={panelId}>
      {note.bodyNative === null ? null : (
        <div className="mynote__row" data-testid={`mynote-native-${note.id}`}>
          {/* Spec 08 §2.3's own framing: the original panel is headed by the language's own
              name in its own script, because it names the manager's own side of the note. */}
          <span className="mynote__lbl">
            {language === undefined ? "Your Words" : `Your Words (${language.endonym})`}
          </span>
          <span
            className="mynote__val mynote__val--native"
            style={language === undefined ? undefined : { fontFamily: language.fontStack }}
          >
            {note.bodyNative}
          </span>
        </div>
      )}

      {/*
       * English is EMPTY, not null, on two kinds of note that are common: a tag-only note
       * (Q-02b — a tap-tag alone is a complete observation) and a native capture whose English
       * has not been made yet (spec 08 — capture sends none). An `English` label over a blank is
       * exactly the rendering failure this list omits rows to avoid.
       */}
      {note.bodyEn.trim() === "" ? null : (
        <div className="mynote__row" data-testid={`mynote-english-${note.id}`}>
          <span className="mynote__lbl">English</span>
          <span className="mynote__val">
            {/* RUN 78a (QA gate) — an AI translation she has not reviewed is not her English yet,
                and the capture screen says so in these words; so does this row. */}
            {note.aiState === "translated" || note.aiState === "stale" ? (
              <>
                <span className="natrev__aitag" data-testid={`mynote-aitag-${note.id}`}>
                  {AI_DRAFT_TAG}
                </span>{" "}
              </>
            ) : null}
            {note.bodyEn}
          </span>
        </div>
      )}

      {/*
       * `Not Yet Categorised` for an OBSERVATION filed under nothing yet — `criterionName` says
       * so, in the words the Match Debrief uses for the same note (run 78a; it read `General`,
       * which is also the name of a note KIND), so on an observation the row is always drawn.
       * Run 72's other two kinds are different (spec 13 §2 and §4): a `general` note is about no
       * umpire, and a `debrief_response` is the umpire's own words and is never filed against a
       * criterion. On those an absent key draws no row, because a criterion row on a debrief
       * response would file the umpire's words as an observation about the umpire. A key the
       * server DID store is shown on any kind: a stored fact is never hidden to keep a list tidy.
       */}
      {note.criterionStableKey === null && note.kind !== "observation" ? null : (
        <div className="mynote__row" data-testid={`mynote-criterion-${note.id}`}>
          <span className="mynote__lbl">Criterion</span>
          <span className="mynote__val">{criterionName(note.criterionStableKey)}</span>
        </div>
      )}

      {note.tagIds.length === 0 ? null : (
        <div className="mynote__row" data-testid={`mynote-tags-${note.id}`}>
          <span className="mynote__lbl">Tags</span>
          <span className="mynote__val mynote__tags">
            {note.tagIds.map((tagId) => (
              <span className="mynote__tag" key={tagId}>
                {tagLabel(tagId)}
              </span>
            ))}
          </span>
        </div>
      )}

      <div className="mynote__row">
        <span className="mynote__lbl">Captured</span>
        <span className="mynote__val num">{tokyoStamp(note.capturedAt)}</span>
      </div>

      {/* Released is a fact only a released note has. A Draft has a `releasedAt` of `null` and
          says nothing about a release that has not happened. */}
      {note.visibility === "released" && note.releasedAt !== null ? (
        <div className="mynote__row" data-testid={`mynote-released-${note.id}`}>
          <span className="mynote__lbl">Released</span>
          <span className="mynote__val num">{tokyoStamp(note.releasedAt)}</span>
        </div>
      ) : null}

      {/*
       * The way out, to the note itself rather than to the top of its match. The capture
       * screen reads the fragment, scrolls to the row and pulses it — see `CapturePanel`.
       * Nothing that CHANGES the note is offered here; this link is how a manager reaches the
       * screen that offers it, and that is the whole of the relationship between the two.
       */}
      <Link className="mynote__jump" to={`${matchHref}#note-${note.id}`}>
        Open In Match ›
      </Link>
    </div>
  );
}

/** The label a tag id renders as — `CapturePanel`'s own fallback, on the same constant. */
function tagLabel(tagId: string): string {
  return CAPTURE_TAP_TAGS.find((tag) => tag.tag_id === tagId)?.label ?? tagId;
}

/**
 * `18 Sep · 14:32` — an instant on the tournament clock, which is the only clock a manager at
 * the venue is reading.
 *
 * It is a local copy for the reason `noteStamp` below is one, and for one more. The UM-side
 * helper that already exists — `tokyoClock` in `SyncChip` — is the TIME alone, which is right
 * for a queue that drained minutes ago and wrong for a note captured on a matchday three days
 * back. The formatter that does fit to the character is `tokyoStamp` in
 * `features/desk/UmActivityPanel.tsx`, and it is on the Technical Delegate's side of the
 * application: the UM app reaching into the TD desk's feature folder for four lines would tie
 * this page to a component tree it must never depend on, and would make the desk's formatting
 * a thing that cannot change without a UM regression. So it is copied, deliberately, exactly
 * as the stamp below is.
 */
function tokyoStamp(iso: string): string {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Tokyo",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(parsed)
    .replace(",", " ·");
}

/**
 * `Q2 · 22′ ·` — the capture screen's own stamp, in the same words.
 *
 * It is a second copy of four lines rather than an import, deliberately: `CapturePanel`'s
 * `stamp` is private to the module that also owns the composer, and exporting it would tie a
 * read-only page to a component that holds every write in the application. If the format ever
 * changes it changes in two places, and both are three lines long.
 */
function noteStamp(note: OwnNoteView): string {
  if (note.quarter === null && note.matchMinute === null) return "Unstamped";
  const quarter = note.quarter === null ? "" : `Q${String(note.quarter)}`;
  const minute = note.matchMinute === null ? "" : `${String(note.matchMinute)}′`;
  return `${[quarter, minute].filter((part) => part !== "").join(" · ")}${
    note.minuteProvisional ? " ·" : ""
  }`;
}

export function UmMyNotesPage(): React.JSX.Element {
  const notes = useAsync(() => fetchMyNotes(), []);
  /**
   * The countdown's tick, for `CapturePanel`'s reason: React re-renders on state, not on the
   * passage of time, so a label computed from `Date.now()` at render would freeze at whatever
   * minute the last unrelated re-render happened in.
   *
   * The interval runs only while a released row still has an open window and stops when the
   * last one closes, so the ordinary case — a page of long-released notes — does no work.
   */
  const [tick, setTick] = useState(() => Date.now());
  /**
   * The whole schedule, for the labels alone. It is a fact about fixtures and never about
   * anybody's notes, and its failure costs the labels and nothing else — see the header.
   */
  const schedule = useAsync(() => fetchFixtureDays(), []);

  const rows = notes.status === "ready" ? notes.data.notes : [];
  const fixtures = schedule.status === "ready" ? schedule.data.fixtures : [];
  const counts = myNotesCounts(rows);
  const days = groupMyNotes(rows, fixtures);

  const now = new Date(tick);
  const undoableCount = rows.filter((note) =>
    note.visibility === "released" ? undoWindowIsOpen(note.releasedAt, now) : false,
  ).length;
  useEffect(() => {
    if (undoableCount === 0) return undefined;
    const timer = setInterval(() => {
      setTick(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [undoableCount]);

  return (
    <>
      <header className="umsect">
        <h2>My Notes</h2>
        <span className="umsect__kicker" data-testid="mynotes-counts">
          {notes.status === "ready" ? myNotesCountLine(counts) : "Whole Tournament"}
        </span>
        <HowToUse id="um-my-notes" title="How to use My Notes">
          <li>
            <HowToLink to="mynotes-days">This list</HowToLink> is every note you have written,
            for the whole tournament, newest match day at the bottom.
          </li>
          <li>
            Each note says whether it is a <b>Draft</b>, <b>Locked</b> or <b>Released</b>. Only
            released notes are visible to the other managers and the Technical Delegate.
          </li>
          <li>
            Tap any note to open it here and read all of it &mdash; your own words in your own
            language, the English, the criterion it is filed under, its tags, and when it was
            captured and released. Tap it again to close it, and open as many as you like.
          </li>
          <li>
            To change a note &mdash; edit it, lock it or release it &mdash; open its match with{" "}
            <b>Open Match ›</b>, or with <b>Open In Match ›</b> inside an opened note, which
            takes you straight to that note. Everything you can do to a note lives on that
            screen.
          </li>
          <li>
            For <b>15 minutes</b> after you release a note you can take the release back. A note
            still inside that window says <b>Undo Available</b> with the time left; open its
            match to press <b>Undo Release</b>. After 15 minutes the release is final, and a
            correction is a new note.
          </li>
          <li>
            The counts above the list are yours. Nobody else is given a total of your drafts or
            your locked notes; the Technical Delegate is told how many notes you have recorded,
            pending and released, and never what they say.
          </li>
        </HowToUse>
      </header>

      {notes.status === "loading" ? <Skeleton rows={5} /> : null}
      {notes.status === "error" ? <FailBanner error={notes.error} /> : null}
      {/*
       * The schedule failing is NOT this screen failing: the notes are all listed, under
       * `Match {id}` headings. The banner says what is missing rather than leaving a manager
       * wondering why the days have no names.
       */}
      {schedule.status === "error" ? (
        <p className="umfoot" role="status" data-testid="mynotes-schedule-failed">
          The match schedule could not be read, so matches are listed by number rather than by
          match day. Every note you have written is still below.
        </p>
      ) : null}

      {/* `ready` and not merely "not loading": with a failed read this screen does not know
          that nothing was captured and must not say so. */}
      {notes.status === "ready" && rows.length === 0 ? (
        <section className="card">
          <p className="card__body" data-testid="mynotes-empty">
            Nothing Captured Yet — Open A Match From Today To Write Your First Note.
          </p>
        </section>
      ) : null}

      {notes.status === "ready" && rows.length > 0 ? (
        <div className="mynotes__days" data-howto="mynotes-days">
          {days.map((day) => (
            <section
              className="card"
              key={day.date ?? "unscheduled"}
              data-testid={`mynotes-day-${day.date ?? "unscheduled"}`}
            >
              <div className="card__head">
                <span className="card__kicker">{day.heading}</span>
                {/* The day's own count only. The four-way breakdown is the page's, at the top:
                    repeating it per day would put a wider figure than the heading beside a
                    heading, and this position is a pill. */}
                <span className="card__note num">{dayCountLabel(day)}</span>
              </div>

              {day.matches.map((match) => (
                <div
                  key={match.matchId}
                  aria-label={match.fullLabel}
                  data-testid={`mynotes-match-${String(match.matchId)}`}
                >
                  <div className="notelist__head">
                    <span className="card__kicker">{match.label}</span>
                    {/*
                     * The one affordance on this page, and it is a way OUT of it. Editing,
                     * locking and releasing live on the capture screen and are not rebuilt
                     * here — see the header.
                     */}
                    <Link className="gbtn gbtn--sm" to={match.href}>
                      Open Match ›
                    </Link>
                  </div>
                  <ul className="notelist">
                    {match.notes.map((note) => (
                      /* The group's own href, so an opened note's `Open In Match ›` lands on
                         the same screen the heading's `Open Match ›` does — with the note's
                         id on the end of it. One computed link, two uses of it. */
                      <NoteRow key={note.id} note={note} now={now} matchHref={match.href} />
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))}
        </div>
      ) : null}
    </>
  );
}
